import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
} from '@/components/ui/table';
import {
  Loader2,
  ShieldCheck,
  RefreshCw,
  FileDown,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

interface AuditorConsoleProps {
  companyId: string;
}

export default function AuditorConsole({ companyId }: AuditorConsoleProps) {
  const [period, setPeriod] = useState(90);
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');

  async function runAudit() {
    setLoading(true);
    setPdfUrl('');
    try {
      const { data, error } = await supabase.functions.invoke('ella-audit-review', {
        body: { companyId, periodDays: period },
      });
      if (error) throw error;
      setReport(data);
      toast.success('Audit completed');
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function reverifyProofs() {
    if (!report?.attachments?.length) return toast('No proofs to verify');
    setVerifying(true);
    try {
      for (const a of report.attachments) {
        await supabase.functions.invoke('audit-proof-verify', {
          body: { refType: a.ref_type, refId: a.ref_id },
        });
      }
      toast.success('Proofs re-verified');
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setVerifying(false);
    }
  }

  async function exportPdf() {
    if (!report) return;
    const { data, error } = await supabase.functions.invoke('ella-audit-pdf', {
      body: { companyId, report },
    });
    if (error) toast.error(error.message);
    else {
      setPdfUrl(data?.url || '');
      toast.success('Audit report generated');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <ShieldCheck className="text-primary" /> AI Auditor (Ella)
        </h2>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={7}
            max={365}
            className="w-24"
            value={period}
            onChange={(e) => setPeriod(parseInt(e.target.value || '90'))}
          />
          <Button variant="outline" size="sm" onClick={runAudit} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-1" />
            )}
            Run Audit
          </Button>
        </div>
      </div>

      {report && (
        <>
          {/* Summary Cards */}
          <div className="grid sm:grid-cols-3 gap-3">
            <Metric title="Trust Score" value={`${report.trustScore ?? 0}/100`} />
            <Metric title="Proof Coverage" value={`${report.proofCoverage?.percent ?? 0}%`} />
            <Metric title="Flags" value={report.flags?.length ?? 0} />
          </div>

          {/* Executive Summary */}
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle>Executive Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="whitespace-pre-wrap text-sm text-muted-foreground">
                {report.executiveSummary || '—'}
              </pre>
            </CardContent>
          </Card>

          {/* Key Findings */}
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle>Key Findings</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Severity</TableHead>
                    <TableHead>Finding</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(report.keyFindings || []).map((f: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell className="uppercase text-xs font-semibold">
                        {f.severity || 'INFO'}
                      </TableCell>
                      <TableCell className="max-w-[520px] whitespace-pre-wrap">{f.text}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Flags */}
          {report.flags?.length > 0 && (
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" /> Flags
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                  {(report.flags || []).map((f: any, i: number) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Remediation */}
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle>Remediation Checklist</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="list-decimal pl-6 text-sm text-muted-foreground space-y-1">
                {(report.remediation || []).map((r: any, i: number) => (
                  <li key={i}>{r}</li>
                ))}
              </ol>
            </CardContent>
          </Card>

          {/* Attachments (proof-backed items) */}
          <Card className="border-border bg-card">
            <CardHeader className="flex items-center justify-between">
              <CardTitle>Proof-Backed Attachments ({report.attachments?.length || 0})</CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={reverifyProofs} disabled={verifying}>
                  {verifying ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                  )}
                  Re-verify Proofs
                </Button>
                <Button size="sm" onClick={exportPdf}>
                  <FileDown className="h-4 w-4 mr-1" /> Export Report
                </Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {report.attachments?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Ref</TableHead>
                      <TableHead>TX</TableHead>
                      <TableHead>Hash</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(report.attachments || []).map((a: any) => (
                      <TableRow key={a.ref_id}>
                        <TableCell>{a.ref_type}</TableCell>
                        <TableCell className="font-mono text-xs">{a.ref_id}</TableCell>
                        <TableCell className="text-xs">
                          {a.tx_hash ? (
                            <a
                              href={a.verifier_url || `https://basescan.org/tx/${a.tx_hash}`}
                              className="text-primary underline"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              tx
                            </a>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell className="text-xs font-mono truncate max-w-[320px]">
                          {a.sha256}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No proof-backed items found in this period.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Link to latest PDF */}
          {pdfUrl && (
            <div className="text-sm">
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                Open Audit Report
              </a>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Metric({ title, value }: { title: string; value: string | number }) {
  return (
    <Card className="border-border bg-card">
      <CardHeader className="pb-1">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}
